/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState } from 'react'

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types'

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from 'react-table'

// @mui material components
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Icon from '@mui/material/Icon'
import Autocomplete from '@mui/material/Autocomplete'

// Material Dashboard 2 React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDInput from 'components/MDInput'
import MDPagination from 'components/MDPagination'

// Material Dashboard 2 React example components
import DataTableHeadCell from 'examples/Tables/DataTable/DataTableHeadCell'
import DataTableBodyCell from 'examples/Tables/DataTable/DataTableBodyCell'

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onDownload,
  exportloading,
}) {
  const defaultValue = entriesPerPage?.defaultValue ? entriesPerPage.defaultValue : 10
  const entries = entriesPerPage?.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ['10', '25', '50', '100']

  const columns = useMemo(() => table.columns, [table])
  const data = useMemo(() => table.rows, [table])

  // For server-side pagination, we don't need react-table's pagination
  const tableInstance = useTable(
    {
      columns,
      data,
      // Disable react-table pagination since we're handling it manually
      initialState: { pageIndex: 0, pageSize: defaultValue },
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy
    // Removed usePagination since we're handling it manually
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    setGlobalFilter,
    state: { globalFilter },
  } = tableInstance

  // Local state for pagination
  const [currentPage, setCurrentPage] = useState(pagination?.pageIndex || 0)
  const [pageSize, setPageSizeState] = useState(defaultValue)

  // Update local state when props change
  useEffect(() => {
    if (pagination?.pageIndex !== undefined) {
      setCurrentPage(pagination.pageIndex)
    }
  }, [pagination?.pageIndex])

  useEffect(() => {
    if (entriesPerPage?.defaultValue !== undefined) {
      setPageSizeState(entriesPerPage.defaultValue)
    }
  }, [entriesPerPage?.defaultValue])

  // Set the entries per page value based on the select value
  const handleEntriesPerPageChange = (value) => {
    const newSize = parseInt(value, 10)
    setPageSizeState(newSize)

    // Only update UI, don't trigger API call
    if (onPageSizeChange) {
      onPageSizeChange(newSize)
    }

    // Reset to first page when changing page size
    //   setCurrentPage(0)
    //   if (onPageChange) {
    //     onPageChange(1) // Page 1 in 1-indexed
    //   }
  }

  // Render the paginations
  const renderPagination = Array.from({ length: pagination?.pageCount || 1 }, (_, i) => i).map(
    (option) => (
      <MDPagination
        item
        key={option}
        onClick={() => handlePageChange(option)}
        active={currentPage === option}
      >
        {option + 1}
      </MDPagination>
    )
  )

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) => {
    const newPage = Math.max(0, Math.min(Number(value) - 1, (pagination?.pageCount || 1) - 1))
    handlePageChange(newPage)
  }

  // Customized page options starting from 1
  const customizedPageOptions = Array.from({ length: pagination?.pageCount || 1 }, (_, i) => i + 1)

  // Search input value state
  const [search, setSearch] = useState('')

  const handleSearchClick = () => {
    if (onSearch) {
      onSearch(search) // call API with search query
    }
  }
  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined)
  }, 100)

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? 'desc' : 'asce'
    } else if (isSorted) {
      sortedValue = 'none'
    } else {
      sortedValue = false
    }

    return sortedValue
  }

  // Handle page changes
  const handlePageChange = (newPageIndex) => {
    setCurrentPage(newPageIndex)
    if (onPageChange) {
      onPageChange(newPageIndex + 1) // Convert to 1-indexed for server
    }
  }

  const handleDownload =  () => {
    if(onDownload){
      onDownload();
    }
  };
  
  // Setting the entries starting point
  const entriesStart = currentPage === 0 ? 1 : currentPage * pageSize + 1

  // Setting the entries ending point
  const entriesEnd = Math.min(entriesStart + pageSize - 1, showTotalEntries || 0)

  // For server-side pagination, we display all rows we received
  const rowsToDisplay = rows

  return (
    <TableContainer sx={{ boxShadow: 'none' }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  handleEntriesPerPageChange(newValue)
                }}
                size="small"
                sx={{ width: '5rem' }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox display="flex" alignItems="center" width="36rem" ml="auto">
              <button
                style={{
                  marginRight: '30px',
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #4CAF50, #43A047)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  backdropFilter: 'blur(6px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: '0.3s',
                  width:"320px"
                }}
                onClick={handleDownload}
                onMouseOver={(e) => (e.target.style.transform = 'translateY(-2px)')}
                onMouseOut={(e) => (e.target.style.transform = 'translateY(0)')}
              >
                {!exportloading ? '📄 Export CSV' : 'download.....'}
              </button>

              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => setSearch(currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchClick() // 👈 also allow Enter
                }}
              />
              <MDBox ml={1}>
                <MDBox
                  component="button"
                  onClick={handleSearchClick}
                  sx={{
                    background: '#1A73E8',
                    border: 'none',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Search
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : 'auto'}
                  align={column.align ? column.align : 'left'}
                  sorted={setSortedValue(column)}
                >
                  {column.render('Header')}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {rowsToDisplay.map((row, key) => {
            prepareRow(row)
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <DataTableBodyCell
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : 'left'}
                    {...cell.getCellProps()}
                  >
                    {cell.render('Cell')}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        p={!showTotalEntries && (pagination?.pageCount || 1) === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {showTotalEntries} entries
            </MDTypography>
          </MDBox>
        )}
        {pagination?.pageCount > 1 && (
          <MDPagination
            variant={pagination?.variant ? pagination.variant : 'gradient'}
            color={pagination?.color ? pagination.color : 'info'}
          >
            {currentPage > 0 && (
              <MDPagination item onClick={() => handlePageChange(currentPage - 1)}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{
                    type: 'number',
                    min: 1,
                    max: customizedPageOptions.length,
                  }}
                  value={customizedPageOptions[currentPage]}
                  onChange={handleInputPagination}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {currentPage < (pagination?.pageCount || 1) - 1 && (
              <MDPagination item onClick={() => handlePageChange(currentPage + 1)}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  )
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: {
    defaultValue: 10,
    entries: [10, 25, 50, 100],
  },
  canSearch: false,
  showTotalEntries: true,
  pagination: {
    variant: 'gradient',
    color: 'info',
    pageCount: 1,
    pageIndex: 0,
  },
  isSorted: true,
  noEndBorder: false,
  onPageChange: undefined,
  onPageSizeChange: undefined,
}

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['contained', 'gradient']),
    color: PropTypes.oneOf([
      'primary',
      'secondary',
      'info',
      'success',
      'warning',
      'error',
      'dark',
      'light',
    ]),
    pageCount: PropTypes.number,
    pageIndex: PropTypes.number,
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
}

export default DataTable
